import * as Popover from "@radix-ui/react-popover";
import type { ReactNode } from "react";

import { cn } from "./lib/cn";

export type ProductSwitcherLabels = {
  menuTitle: string;
  shop: string;
  checklists: string;
  flowManager?: string;
  triggerAria: string;
  currentBadge?: string;
};

export type ProductSwitcherProps = {
  current: "shop" | "checklisten" | "flow-manager";
  shopUrl: string;
  checklistenUrl: string;
  flowManagerUrl?: string;
  labels: ProductSwitcherLabels;
  className?: string;
};

function GridIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="5" cy="5" r="1.75" />
      <circle cx="10" cy="5" r="1.75" />
      <circle cx="15" cy="5" r="1.75" />
      <circle cx="5" cy="10" r="1.75" />
      <circle cx="10" cy="10" r="1.75" />
      <circle cx="15" cy="10" r="1.75" />
      <circle cx="5" cy="15" r="1.75" />
      <circle cx="10" cy="15" r="1.75" />
      <circle cx="15" cy="15" r="1.75" />
    </svg>
  );
}

function ShopAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8.25 8.25V6a3.75 3.75 0 0 1 7.5 0v2.25" />
      <path d="M3.75 9h16.5l-1.22 11.02a1.5 1.5 0 0 1-1.49 1.23H6.46a1.5 1.5 0 0 1-1.49-1.23L3.75 9Z" />
    </svg>
  );
}

function ChecklistsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 2.5h6a.5.5 0 0 1 .5.5v1.5H17a1.5 1.5 0 0 1 1.5 1.5V20A1.5 1.5 0 0 1 17 21.5H7A1.5 1.5 0 0 1 5.5 20V6A1.5 1.5 0 0 1 7 4.5h1.5V3a.5.5 0 0 1 .5-.5Z" />
      <path d="m9.75 11.25 1.75 1.75 3.5-3.5" />
      <path d="M9 16.25h6" />
    </svg>
  );
}

function FlowManagerAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 6.5h16" />
      <path d="M4 12h10" />
      <path d="M4 17.5h14" />
      <circle cx="18" cy="12" r="2.25" />
      <path d="M18 9.75V7.5" />
      <path d="M18 16.5v-2.25" />
    </svg>
  );
}

function AppRow(props: {
  icon: ReactNode;
  title: string;
  href?: string;
  isCurrent: boolean;
  currentBadge?: string;
}) {
  const { icon, title, href, isCurrent, currentBadge } = props;

  const rowClass = cn(
    "flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors",
    isCurrent
      ? "cursor-default bg-neutral-100/90 text-neutral-700 dark:bg-neutral-800/80 dark:text-neutral-200"
      : "text-neutral-900 hover:bg-neutral-100/80 dark:text-neutral-100 dark:hover:bg-neutral-800/60",
  );

  const iconWell = cn(
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-md [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]",
    isCurrent
      ? "bg-white text-neutral-600 shadow-sm dark:bg-neutral-900 dark:text-neutral-300"
      : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
  );

  const body = (
    <>
      <span className={iconWell}>{icon}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium leading-tight">{title}</span>
        {isCurrent && currentBadge ? (
          <span className="text-xs font-normal text-neutral-500 dark:text-neutral-400">
            {currentBadge}
          </span>
        ) : null}
      </span>
    </>
  );

  if (isCurrent) {
    return (
      <div className={rowClass} aria-current="page">
        {body}
      </div>
    );
  }

  return (
    <a href={href} className={cn(rowClass, "no-underline")}>
      {body}
    </a>
  );
}

export function ProductSwitcher({
  current,
  shopUrl,
  checklistenUrl,
  flowManagerUrl,
  labels,
  className,
}: ProductSwitcherProps) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-400 dark:text-neutral-300 dark:hover:bg-neutral-800",
            className,
          )}
          aria-label={labels.triggerAria}
        >
          <GridIcon className="h-5 w-5 opacity-80" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="start"
          className="z-50 w-[min(17rem,calc(100vw-1.5rem))] rounded-xl border border-neutral-200/80 bg-white p-1.5 shadow-md shadow-neutral-900/5 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 dark:border-neutral-700/80 dark:bg-neutral-950 dark:shadow-black/20"
        >
          <p className="px-2 pb-1 pt-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {labels.menuTitle}
          </p>
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            <li>
              <AppRow
                icon={<ShopAppIcon />}
                title={labels.shop}
                href={shopUrl}
                isCurrent={current === "shop"}
                currentBadge={labels.currentBadge}
              />
            </li>
            <li>
              <AppRow
                icon={<ChecklistsAppIcon />}
                title={labels.checklists}
                href={checklistenUrl}
                isCurrent={current === "checklisten"}
                currentBadge={labels.currentBadge}
              />
            </li>
            {flowManagerUrl && labels.flowManager ? (
              <li>
                <AppRow
                  icon={<FlowManagerAppIcon />}
                  title={labels.flowManager}
                  href={flowManagerUrl}
                  isCurrent={current === "flow-manager"}
                  currentBadge={labels.currentBadge}
                />
              </li>
            ) : null}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
